@if(isLoading()) {
  <!-- ═══════════════════════════════════════════ DESKTOP LAYOUT ═══════════════════════════════════════════ -->
  @if(desktopLayout()) {
    <div class="widget-container widget-desktop">

      <!-- Left: chart panel -->
      <div class="desktop-chart-panel">
        @if(activeView() === 'chart') {
          <div class="chart-legend">
            @for(item of legendItems(); track item.label) {
              <div class="legend-item">
                <span class="legend-box" [style.background]="item.color"></span>
                <span class="legend-label">{{ item.label }}</span>
              </div>
            }
          </div>

          <div #chartWrapper class="chart-wrapper">
            @if(isBrowser) {
              <canvas #chartCanvas class="widget-canvas" baseChart
                [data]="chartData()" [options]="barChartOptionsDesktop" [type]="barChartType" [plugins]="chartPlugins"
                (touchstart)="onChartTouchStart($event)"
                (touchend)="onChartTouchEnd($event)">
              </canvas>
            } @else {
              <div class="widget-canvas"></div>
            }

            @if(isLoadingChart()) {
              <div class="chart-loading-overlay">
                <ion-spinner name="crescent"></ion-spinner>
              </div>
            }
            @if(hasNoData()) {
              <div class="chart-no-data-overlay">
                <span>{{ 'COMMON.NO_DATA' | translate }}</span>
              </div>
            }
          </div>

          @if(committedTimeRange() === 'DayQuarter' && totalQuarterPages() > 1) {
            <div class="quarter-page-dots">
              @for(page of quarterPages(); track page) {
                <button
                  class="quarter-dot"
                  [class.quarter-dot-active]="page === currentQuarterPage()"
                  (click)="goToPage(page)"
                  [attr.aria-label]="'Pagina ' + (page + 1) + ' van ' + totalQuarterPages()">
                </button>
              }
            </div>
          }
        }

        @if(activeView() === 'table') {
          <div #tableWrapper class="price-table-wrapper desktop-table-wrapper">
            <table class="price-table">
              <thead>
                <tr>
                  <th>{{ 'COMMON.TIME' | translate }}</th>
                  <th>
                    @if(activeMarketType() === 'EL') { {{ 'COMMON.PRICES.ELECTRIC' | translate }} }
                    @else if(activeMarketType() === 'GAS') { {{ 'COMMON.PRICES.GAS' | translate }} }
                    @else { {{ 'COMMON.PRICES.IMBALANCE' | translate }} }
                  </th>
                </tr>
              </thead>
              <tbody>
                @for(item of tableItems(); track item.date) {
                  <tr [class.price-min]="isTableMinPrice(item.price)"
                      [class.price-max]="isTableMaxPrice(item.price)">
                    <td>{{ formatItemDate(item) }}</td>
                    <td>{{ item.price | currency:'EUR':'symbol':'1.2-2':'nl-BE' }}</td>
                  </tr>
                }
              </tbody>
              @if(showAverage()) {
                <tfoot>
                  <tr class="average-row">
                    <td>{{ 'COMMON.AVERAGE' | translate }}</td>
                    <td>{{ tableAverageValue() | currency:'EUR':'symbol':'1.2-2':'nl-BE' }}</td>
                  </tr>
                </tfoot>
              }
            </table>
          </div>
        }

        @if(activeMarketType() === 'IMBALANCE') {
          @if(lastUpdatedDisplay()) {
            <div class="last-updated">
              {{ 'COMMON.UPDATED_AT' | translate: { time: lastUpdatedDisplay()!.toFormat('dd/MM/yyyy HH:mm') } }}
            </div>
          }
          <div class="imbalance-disclaimer">
            {{ 'WIDGETS.DAYAHEAD.DISCLAIMER' | translate }}
          </div>
        }

        <!-- kWh/MWh at bottom of chart panel -->
        <div class="desktop-bottom-row">
          <ion-segment mode="ios" class="unit-segment" [value]="activeVolume()" (ionChange)="viewChange($event)" [swipeGesture]="false">
            <ion-segment-button [value]="volumeEnum.KWh">kWh</ion-segment-button>
            <ion-segment-button [value]="volumeEnum.MWh">MWh</ion-segment-button>
          </ion-segment>
        </div>
      </div>

      <!-- Right: controls panel -->
      <div class="desktop-controls-panel">

        <!-- Market type tabs -->
        <ion-segment mode="ios" color="light-green" [value]="activeMarketType()" (ionChange)="marketChange($event)" [swipeGesture]="false">
          <ion-segment-button value="EL">
            <ion-icon src="/assets/icon/lightning.svg"></ion-icon>
          </ion-segment-button>
          <ion-segment-button value="GAS">
            <ion-icon src="/assets/icon/gas.svg"></ion-icon>
          </ion-segment-button>
          <ion-segment-button value="IMBALANCE">
            <ion-icon src="/assets/icon/balance.svg"></ion-icon>
          </ion-segment-button>
        </ion-segment>

        <!-- View toggle + time range dropdown -->
        <div class="top-controls-row desktop-top-controls">
          <div class="view-toggle-buttons">
            <button class="toggle-btn" [class.active]="activeView() === 'chart'" (click)="activeView.set('chart')"
              [title]="'COMMON.CHART' | translate">
              <ion-icon src="/assets/icon/chart-view.svg"></ion-icon>
            </button>
            <button class="toggle-btn" [class.active]="activeView() === 'table'" (click)="activeView.set('table')"
              [title]="'COMMON.TABLE' | translate">
              <ion-icon src="/assets/icon/table-view.svg"></ion-icon>
            </button>
          </div>

          <div class="time-range-dropdown">
            <button class="time-range-trigger desktop-time-trigger" (click)="toggleTimeRangeDropdown()">
              <span class="time-range-trigger-label">{{ getTimeRangeLabel() }}</span>
              <ion-icon [name]="isTimeRangeDropdownOpen() ? 'chevron-up' : 'chevron-down'" class="time-range-trigger-icon"></ion-icon>
            </button>
            @if(isTimeRangeDropdownOpen()) {
              <div class="time-range-options">
                @if(activeMarketType() !== 'GAS') {
                  <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'DayQuarter'"
                    (click)="selectTimeRange('DayQuarter')">{{ 'COMMON.DAY_QUARTER' | translate }}</button>
                  <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'DayHour'"
                    (click)="selectTimeRange('DayHour')">{{ 'COMMON.DAY_HOUR' | translate }}</button>
                }
                <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'Week'"
                  (click)="selectTimeRange('Week')">{{ 'COMMON.WEEK' | translate }}</button>
                <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'Month'"
                  (click)="selectTimeRange('Month')">{{ 'COMMON.MONTH' | translate }}</button>
                <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'Year'"
                  (click)="selectTimeRange('Year')">{{ 'COMMON.YEAR' | translate }}</button>
              </div>
            }
          </div>
        </div>

        <!-- Inline calendar -->
        <div class="desktop-calendar">
          <div class="desktop-calendar-nav">
            <button class="desktop-cal-nav-btn" (click)="prevCalendarMonth()" [disabled]="isPrevCalendarMonthDisabled()">
              <ion-icon name="chevron-back"></ion-icon>
            </button>
            <span class="desktop-cal-month-label">{{ desktopCalendarHeader() }}</span>
            <button class="desktop-cal-nav-btn" (click)="nextCalendarMonth()" [disabled]="isNextCalendarMonthDisabled()">
              <ion-icon name="chevron-forward"></ion-icon>
            </button>
          </div>

          @switch(activeFilterRange()) {

            @case('Day') {
              <!-- 7-column day grid -->
              <div class="desktop-calendar-grid">
                @for(d of ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']; track d) {
                  <span class="desktop-cal-weekday">{{ d }}</span>
                }
                @for(day of desktopCalendarDays(); track $index) {
                  @if(day) {
                    <button class="desktop-cal-day"
                      [class.selected]="isDesktopDaySelected(day)"
                      [class.disabled]="isDateDisabled(day)"
                      [disabled]="isDateDisabled(day)"
                      (click)="selectDesktopDay(day)">
                      {{ day.day }}
                    </button>
                  } @else {
                    <span class="desktop-cal-day-empty"></span>
                  }
                }
              </div>
            }

            @case('Week') {
              <!-- 3-column week grid -->
              <div class="desktop-picker-grid">
                @for(week of desktopCalendarWeeks(); track $index) {
                  <button class="desktop-picker-item"
                    [class.selected]="isDesktopPickerItemSelected(week)"
                    [class.disabled]="isDesktopPickerItemDisabled(week)"
                    [disabled]="isDesktopPickerItemDisabled(week)"
                    (click)="selectDesktopPickerItem(week)">
                    {{ getWeekLabel(week) }}
                  </button>
                }
              </div>
            }

            @case('Month') {
              <!-- 3-column month grid -->
              <div class="desktop-picker-grid">
                @for(month of desktopCalendarMonths(); track $index) {
                  <button class="desktop-picker-item"
                    [class.selected]="isDesktopPickerItemSelected(month)"
                    [class.disabled]="isDesktopPickerItemDisabled(month)"
                    [disabled]="isDesktopPickerItemDisabled(month)"
                    (click)="selectDesktopPickerItem(month)">
                    {{ getMonthLabel(month) }}
                  </button>
                }
              </div>
            }

            @case('Year') {
              <!-- 3-column year grid -->
              <div class="desktop-picker-grid">
                @for(year of desktopCalendarYears(); track $index) {
                  <button class="desktop-picker-item"
                    [class.selected]="isDesktopPickerItemSelected(year)"
                    [class.disabled]="isDesktopPickerItemDisabled(year)"
                    [disabled]="isDesktopPickerItemDisabled(year)"
                    (click)="selectDesktopPickerItem(year)">
                    {{ getYearLabel(year) }}
                  </button>
                }
              </div>
            }

          }
        </div>

        <!-- Action buttons -->
        <div class="desktop-actions">
          <button class="toggle-btn" [class.active]="showAverage()" (click)="showAverage.set(!showAverage())"
            [title]="'COMMON.AVERAGE' | translate">
            <ion-icon src="/assets/icon/average.svg"></ion-icon>
          </button>

          <!-- Share button -->
          <div class="desktop-share-wrapper">
            <button class="toggle-btn" [class.active]="isShareDropdownOpen()" (click)="openShareSheet()"
              [title]="'COMMON.SHARE' | translate">
              <ion-icon name="arrow-up-right-box-outline"></ion-icon>
            </button>
          </div>
        </div>

      </div>
    </div>

  <!-- ═══════════════════════════════════════════ MOBILE LAYOUT ═══════════════════════════════════════════ -->
  } @else {
    <div class="widget-container">
      <ion-segment mode="ios" value="EL" color="light-green" (ionChange)="marketChange($event)" [swipeGesture]="false">
        <ion-segment-button value="EL">
          <ion-icon src="/assets/icon/lightning.svg"></ion-icon>
        </ion-segment-button>
        <ion-segment-button value="GAS">
          <ion-icon src="/assets/icon/gas.svg"></ion-icon>
        </ion-segment-button>
        <ion-segment-button value="IMBALANCE">
          <ion-icon src="/assets/icon/balance.svg"></ion-icon>
        </ion-segment-button>
      </ion-segment>

      <div class="widget-content">
        <div class="top-controls-row">
          <div class="view-toggle-buttons">
            <button class="toggle-btn" [class.active]="activeView() === 'chart'" (click)="activeView.set('chart')"
              [title]="'COMMON.CHART' | translate">
              <ion-icon src="/assets/icon/chart-view.svg"></ion-icon>
            </button>
            <button class="toggle-btn" [class.active]="activeView() === 'table'" (click)="activeView.set('table')"
              [title]="'COMMON.TABLE' | translate">
              <ion-icon src="/assets/icon/table-view.svg"></ion-icon>
            </button>
          </div>

          <div class="time-range-dropdown">
            <button class="time-range-trigger" (click)="toggleTimeRangeDropdown()">
              <span class="time-range-trigger-label">{{ getTimeRangeLabel() }}</span>
              <ion-icon [name]="isTimeRangeDropdownOpen() ? 'chevron-up' : 'chevron-down'" class="time-range-trigger-icon"></ion-icon>
            </button>
            @if(isTimeRangeDropdownOpen()) {
              <div class="time-range-options">
                @if(activeMarketType() !== 'GAS') {
                  <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'DayQuarter'"
                    (click)="selectTimeRange('DayQuarter')">{{ 'COMMON.DAY_QUARTER' | translate }}</button>
                  <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'DayHour'"
                    (click)="selectTimeRange('DayHour')">{{ 'COMMON.DAY_HOUR' | translate }}</button>
                }
                <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'Week'"
                  (click)="selectTimeRange('Week')">{{ 'COMMON.WEEK' | translate }}</button>
                <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'Month'"
                  (click)="selectTimeRange('Month')">{{ 'COMMON.MONTH' | translate }}</button>
                <button class="time-range-option" [class.time-range-option-active]="activeTimeRange() === 'Year'"
                  (click)="selectTimeRange('Year')">{{ 'COMMON.YEAR' | translate }}</button>
              </div>
            }
          </div>
        </div>

        <div class="widget-date-navigation">
          <ion-button fill="clear" color="secondary" (click)="goBackward()">
            <ion-icon slot="icon-only" color="secondary" name="chevron-back"></ion-icon>
          </ion-button>
          <ion-text color="secondary" class="widget-date-display" (click)="rangePickerModal.present()">
            {{ currentTimeDisplay }}
          </ion-text>
          <ion-button fill="clear" color="secondary" (click)="goForward()">
            <ion-icon slot="icon-only" color="secondary" name="chevron-forward"></ion-icon>
          </ion-button>
          <div class="widget-date-button"></div>
        </div>

        <!-- Fixed-height area: keeps widget height identical whether chart or table is active -->
        <div class="mobile-view-area">
          @if(activeView() === 'chart') {
            <div class="mobile-chart-view">
              <div class="chart-legend">
                @for(item of legendItems(); track item.label) {
                  <div class="legend-item">
                    <span class="legend-box" [style.background]="item.color"></span>
                    <span class="legend-label">{{ item.label }}</span>
                  </div>
                }
              </div>

              <div #chartWrapper class="chart-wrapper">
                @if(isBrowser) {
                  <canvas #chartCanvas class="widget-canvas" baseChart
                    [data]="chartData()" [options]="barChartOptions" [type]="barChartType" [plugins]="chartPlugins"
                    (touchstart)="onChartTouchStart($event)"
                    (touchend)="onChartTouchEnd($event)">
                  </canvas>
                } @else {
                  <div class="widget-canvas"></div>
                }

                @if(isLoadingChart()) {
                  <div class="chart-loading-overlay">
                    <ion-spinner name="crescent"></ion-spinner>
                  </div>
                }
                @if(hasNoData()) {
                  <div class="chart-no-data-overlay">
                    <span>{{ 'COMMON.NO_DATA' | translate }}</span>
                  </div>
                }
              </div>

            </div>
          }

          @if(activeView() === 'table') {
            <div #tableWrapper class="price-table-wrapper">
              <table class="price-table">
                <thead>
                  <tr>
                    <th>{{ 'COMMON.TIME' | translate }}</th>
                    <th>
                      @if(activeMarketType() === 'EL') { {{ 'COMMON.PRICES.ELECTRIC' | translate }} }
                      @else if(activeMarketType() === 'GAS') { {{ 'COMMON.PRICES.GAS' | translate }} }
                      @else { {{ 'COMMON.PRICES.IMBALANCE' | translate }} }
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for(item of tableItems(); track item.date) {
                    <tr [class.price-min]="isTableMinPrice(item.price)"
                        [class.price-max]="isTableMaxPrice(item.price)">
                      <td>{{ formatItemDate(item) }}</td>
                      <td>{{ item.price | currency:'EUR':'symbol':'1.2-2':'nl-BE' }}</td>
                    </tr>
                  }
                </tbody>
                @if(showAverage()) {
                  <tfoot>
                    <tr class="average-row">
                      <td>{{ 'COMMON.AVERAGE' | translate }}</td>
                      <td>{{ tableAverageValue() | currency:'EUR':'symbol':'1.2-2':'nl-BE' }}</td>
                    </tr>
                  </tfoot>
                }
              </table>
            </div>
          }
        </div>

        @if(activeView() === 'chart' && committedTimeRange() === 'DayQuarter' && totalQuarterPages() > 1) {
          <div class="quarter-page-dots">
            @for(page of quarterPages(); track page) {
              <button
                class="quarter-dot"
                [class.quarter-dot-active]="page === currentQuarterPage()"
                (click)="goToPage(page)"
                [attr.aria-label]="'Pagina ' + (page + 1) + ' van ' + totalQuarterPages()">
              </button>
            }
          </div>
        }

        @if(activeMarketType() === 'IMBALANCE') {
          @if(lastUpdatedDisplay()) {
            <div class="last-updated">
              {{ 'COMMON.UPDATED_AT' | translate: { time: lastUpdatedDisplay()!.toFormat('dd/MM/yyyy HH:mm') } }}
            </div>
          }
          <div class="imbalance-disclaimer">
            {{ 'WIDGETS.DAYAHEAD.DISCLAIMER' | translate }}
          </div>
        }

        <div class="widget-controls-row">

          <ion-segment mode="ios" class="unit-segment" [value]="activeVolume()" (ionChange)="viewChange($event)" [swipeGesture]="false">
            <ion-segment-button [value]="volumeEnum.KWh">kWh</ion-segment-button>
            <ion-segment-button [value]="volumeEnum.MWh">MWh</ion-segment-button>
          </ion-segment>

          <div class="action-buttons">
            <button class="toggle-btn" [class.active]="showAverage()" (click)="showAverage.set(!showAverage())"
              [title]="'COMMON.AVERAGE' | translate">
              <ion-icon src="/assets/icon/average.svg"></ion-icon>
            </button>
            <!-- Share: dropdown for browser/website, ActionSheet for native app only -->
            <div class="desktop-share-wrapper">
              <button class="toggle-btn" [class.active]="isShareDropdownOpen()" (click)="openShareSheet()"
                [title]="'COMMON.SHARE' | translate">
                <ion-icon name="arrow-up-right-box-outline"></ion-icon>
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  }

}

<!-- ── Date range picker modal (mobile only) ── -->
<ion-modal id="range-picker" #rangePickerModal>
  <ng-template>
    <div class="modal-wrapper">
      <div class="range-picker-navigation">
        <ion-button fill="outline" color="secondary" (click)="goBackward()">
          <ion-icon slot="icon-only" color="secondary" name="chevron-back"></ion-icon>
        </ion-button>
        <ion-text color="secondary" class="widget-date-display">
          {{ currentTimeDisplay }}
        </ion-text>
        <ion-button fill="outline" color="secondary" (click)="goForward()">
          <ion-icon slot="icon-only" color="secondary" name="chevron-forward"></ion-icon>
        </ion-button>
      </div>
      <div class="range-picker-grid" [class]="activeFilterRange()">
        @for(date of rangePickerDates; track date) {
          <div class="range-picker-date"
            [class]="{'active-date': date.equals(currentChartRange.start), 'disabled': isDateDisabled(date)}"
            (click)="updateCurrentDate(date)">
            {{ formatPickerDate(date) }}
          </div>
        }
      </div>
    </div>
  </ng-template>
</ion-modal>

<!-- ── Share / download dialog (web only) ── -->
<!-- Native <dialog> renders in the browser top layer — unaffected by ancestor
     overflow:hidden, CSS transforms, or stacking contexts. -->
<dialog #shareDialog class="share-dialog" (click)="onShareDialogBackdropClick($event)" (close)="closeShareModal()">
  <div class="share-modal-content">
    <div class="share-modal-header">
      <span class="share-modal-title">{{ 'COMMON.SHARE_OR_DOWNLOAD' | translate }}</span>
      <button class="share-modal-close" (click)="closeShareModal()" type="button">
        <ion-icon name="close"></ion-icon>
      </button>
    </div>

    <!-- Web only. The native app never opens this dialog — openShareSheet() gives
         it an Ionic ActionSheet instead, which keeps its own share options. -->
    <div class="share-actions">
      <button class="share-download-btn" (click)="exportPdfAction()" type="button">
        {{ 'COMMON.DOWNLOAD_PDF' | translate }}
      </button>

      <button class="share-download-btn" (click)="downloadImageAction()" type="button">
        {{ 'COMMON.DOWNLOAD_IMAGE' | translate }}
      </button>

      <button class="share-download-btn share-download-btn--outline" (click)="copyLink()" type="button">
        @if (copyLinkSuccess()) {
          <ion-icon name="checkmark-outline"></ion-icon>
          {{ 'COMMON.COPIED' | translate }}
        } @else {
          {{ 'COMMON.COPY_URL' | translate }}
        }
      </button>
    </div>
  </div>
</dialog>
